/* eslint-disable no-nested-ternary */
import { LocalMeteoSummaryLoader, LocalMeteoSummaryProps } from '../LocalMeteoSummary/LocalMeteoSummary.component';
import { MetricsLayoutLoader, MetricsLayoutProps } from '../MetricsLayout/MetricsLayout.component';
import { MeteoHomeContext } from 'pages/meteo/[type]/[locality]';
import { TimeStep, TimeStep24H } from 'pleinchamp-api-client';
import React, { FC, useContext, useEffect, useState } from 'react';
import { usePlcApiCall } from '@api/business/api.hook';
import { CommunityForecastButton } from '@community/components/CommunityForecastButton/CommunityForecastButton.component';
import { SecondaryButton } from '@components/buttons/FlatButton/FlatButton.component';
import { MapComponentProps } from '@components/MapComponent/Map.types';
import { MapComponentLoader } from '@components/MapComponent/MapLoader.component';
import { PlcMessageWithRetryProps } from '@components/PlcMessage/PlcMessageWithRetry.component';
import { Text } from '@components/Text/Text.component';
import { PlcAnchor } from '@layout/components/PlcAnchor/PlcAnchor.component';
import { useForecastParamCodes } from '@meteo/business/Forecast.hook';
import { useLocation } from '@meteo/business/LocationSelector.hook';
import { fetchForecasts } from '@meteo/business/Meteo.api';
import { useWeatherLink } from '@meteo/business/meteoUrl.utils';
import {
  MeteoCityAutocompleteLoader,
  MeteoCityAutocompleteProps,
} from '@meteo/components/MeteoCityAutocomplete/MeteoCityAutocomplete.component';
import { MeteoHomeSummaryNoCityProps } from '@meteo/components/MeteoHomeSummaryNoCity/MeteoHomeSummaryNoCity.component';
import { Breakpoint, useIsBreakpointDown } from '@styles/breakpoints';
import { usePlcRouter } from '@utils/customHooks';
import { isServer } from '@utils/server';
import { urlEnum } from '@utils/url';
import dynamic from 'next/dynamic';

import './MeteoHomeSummary.scss';

const MeteoCityAutocomplete = dynamic<MeteoCityAutocompleteProps>(
  () =>
    import('@meteo/components/MeteoCityAutocomplete/MeteoCityAutocomplete.component').then(
      mod => mod.MeteoCityAutocomplete
    ),
  {
    loading: () => <MeteoCityAutocompleteLoader />,
    ssr: false,
  }
);

const MeteoHomeSummaryNoCity = dynamic<MeteoHomeSummaryNoCityProps>(() =>
  import('@meteo/components/MeteoHomeSummaryNoCity/MeteoHomeSummaryNoCity.component').then(
    mod => mod.MeteoHomeSummaryNoCity
  )
);

const LocalMeteoSummary = dynamic<LocalMeteoSummaryProps>(
  () => import('@meteo/components/LocalMeteoSummary/LocalMeteoSummary.component').then(mod => mod.LocalMeteoSummary),
  {
    loading: () => <LocalMeteoSummaryLoader uniqueKey="localMeteoSummary" />,
    ssr: false,
  }
);

const MetricsLayout = dynamic<MetricsLayoutProps>(
  () => import('@meteo/components/MetricsLayout/MetricsLayout.component').then(mod => mod.MetricsLayout),
  {
    loading: () => <MetricsLayoutLoader />,
    ssr: false,
  }
);

const PlcMessageWithRetry = dynamic<PlcMessageWithRetryProps>(
  () => import('@components/PlcMessage/PlcMessageWithRetry.component').then(mod => mod.PlcMessageWithRetry),
  { ssr: false }
);

const MapComponent = dynamic<MapComponentProps>(
  () => import('@components/MapComponent/Map.component').then(mod => mod.MapComponent),
  {
    loading: () => <MapComponentLoader />,
    ssr: false,
  }
);

const MeteoHomeSummary: FC = () => {
  const {
    city: { setData: setCity },
    forecasts: { initialData, setData: setForecasts },
    additionalInfos,
    reversedCity,
  } = useContext(MeteoHomeContext);
  const isMobile = useIsBreakpointDown(Breakpoint.m);
  const [displayRadar, setDisplayRadar] = useState(isMobile);
  const { coordinates, geolocationError } = useLocation(reversedCity);
  const paramCodes = useForecastParamCodes();

  const {
    data: forecasts,
    error: forecastError,
    isValidating: loading,
    retryCallback: retryForecast,
  } = usePlcApiCall(fetchForecasts, [coordinates, paramCodes], { initialData });

  if (forecasts && forecasts !== initialData && setForecasts) {
    setForecasts(forecasts);
  }

  const isLoadingOrServer = loading || isServer();

  const dayForecast: TimeStep24H | false =
    !loading && !forecastError && forecasts && forecasts.timeStep24 ? forecasts.timeStep24[0] : false;

  // Nous récupéré 3 valeurs pour un timestep de 1 et nous voulons l'heure courante + 1.
  // Il s'agit donc du deuxième élément du tableau.
  const realTimeForecast: TimeStep | false =
    !loading && !forecastError && forecasts && forecasts.timeStep1 ? forecasts.timeStep1[0] : false;
  const radarLink = useWeatherLink(urlEnum.meteoRadar);

  const router = usePlcRouter();

  useEffect(() => {
    setDisplayRadar(!isMobile);
  }, [isMobile]);

  return (
    <div className="meteo-home-summary">
      <div className="meteo-home-summary-wrapper">
        <div className="meteo-home-summary-search plc-pt-basis">
          {!!reversedCity && (
            <MeteoCityAutocomplete
              current={reversedCity}
              inputId="meteo-city-autocomplete-input-with-city"
              // eslint-disable-next-line react/jsx-no-bind
              onSelect={c => (c && setCity ? setCity(c) : undefined)}
              withConnectSuggestion
            />
          )}
        </div>
        <div className="meteo-home-summary-main">
          {!reversedCity && <MeteoHomeSummaryNoCity setCity={setCity} />}
          {dayForecast && realTimeForecast && additionalInfos && (
            <div>
              <LocalMeteoSummary
                dateTime={realTimeForecast.date}
                sunrise={additionalInfos.sunrise}
                sunset={additionalInfos.sunset}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                temperature={realTimeForecast.airTemperatureInCelsius!}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                weatherCode={realTimeForecast.weatherCode!}
              />
              <MetricsLayout
                humidity={dayForecast.averageRelativeHumidityInPercent}
                isLoading={Boolean(loading)}
                maxTemp={dayForecast.maxAirTemperatureInCelsius}
                minTemp={dayForecast.minAirTemperatureInCelsius}
                moonPhase={additionalInfos.moonPhase}
                rainLevel={dayForecast.precipitationAmountInMillimeter}
                sunrise={additionalInfos.sunrise}
                sunset={additionalInfos.sunset}
                windAngle={dayForecast.dominantWindDirectionInDegree}
                windDirection={dayForecast.dominantWindDirection}
                windSpeed={dayForecast.averageWindSpeedInKilometerPerHour2}
              />
            </div>
          )}
          {!isLoadingOrServer && reversedCity && (forecastError || geolocationError || !dayForecast) && (
            <div>
              <PlcMessageWithRetry isLoading={loading} retryCallback={retryForecast} />
            </div>
          )}
        </div>
        <div className="meteo-home-summary-buttons">
          <PlcAnchor className="flex" linkProps={radarLink}>
            <SecondaryButton
              className="plc-mb-m"
              textProps={{ i18nKey: 'meteo.navigate-to-radar' }}
              wrapperClassName="none inline-flex-up-m"
            />
          </PlcAnchor>
          <Text className="plc-mb-s" i18nKey="meteo.navigate-to-radar.prefix" variant="tiny" />
          <CommunityForecastButton className="plc-mb-m plc-mb-up-m-0" wrapperClassName="flex" />
          <PlcAnchor className="flex" linkProps={radarLink}>
            <SecondaryButton textProps={{ i18nKey: 'meteo.navigate-to-radar' }} wrapperClassName="none-up-m flex-1" />
          </PlcAnchor>
        </div>
        {displayRadar && (
          <MapComponent
            className="meteo-home-summary-radar none block-up-m"
            geoPoint={coordinates}
            hasRadar
            isCompact
            // eslint-disable-next-line react/jsx-no-bind
            onClick={() => router.push(radarLink.href, radarLink.as)}
          />
        )}
      </div>
    </div>
  );
};

export { MeteoHomeSummary };
